import { useState, forwardRef, useImperativeHandle, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, CheckCircle2, Lock, Gift } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import Brand from "../components/Brand";

/* LeadCapture — simple "drop your email, get a free Faitharama membership"
   capture form. Replaces the old free-guide / flipbook flow that was tied
   to the deleted books. */
const LeadCapture = forwardRef(function LeadCapture({ source = "free-membership" }, ref) {
  const [form, setForm] = useState({ name: "", email: "" });
  const [done, setDone] = useState(false);
  const [tempPw, setTempPw] = useState("");
  const [newMember, setNewMember] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const emailRef = useRef(null);
  const nav = useNavigate();

  useImperativeHandle(ref, () => ({
    focus: () => {
      if (wrapRef.current) wrapRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => { emailRef.current?.focus(); }, 450);
    },
  }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/leads/free-guide", { ...form, source });
      setDone(true);
      setNewMember(!!r.data?.new_member);
      if (r.data?.delivered === "screen" && r.data?.temp_password) setTempPw(r.data.temp_password);
      toast.success("You're in — welcome to Faitharama!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div ref={wrapRef} id="guide-form" className="faith-card p-6 border-2 text-center" style={{ borderColor: "#F39A1F" }} data-testid="lead-capture-success">
        <CheckCircle2 className="w-10 h-10 text-gold-deep mx-auto" strokeWidth={1.75} />
        <div className="mt-3 text-2xl uppercase text-red-wine">Thank you, {form.name.split(" ")[0]}</div>
        <p className="text-sm text-foreground mt-2">
          {newMember
            ? "Your free Faitharama membership is ready. Sign in to start exploring."
            : "We've got your details — check your inbox."}
        </p>
        {tempPw && (
          <div className="mt-3 rounded-md p-3 bg-white border border-[#F39A1F]">
            <div className="text-[11px] tracking-wider-cap font-bold text-gold-deep">Your Temporary Password</div>
            <div className="font-mono text-base text-red-wine mt-1">{tempPw}</div>
          </div>
        )}
        <button type="button" onClick={() => nav("/login")}
                className="btn-gold inline-flex items-center gap-2 !py-2.5 !px-5 mt-4 !text-[12px]"
                data-testid="lead-go-login">
          Go To Sign In
        </button>
      </div>
    );
  }

  return (
    <form ref={wrapRef} id="guide-form" onSubmit={submit} className="faith-card p-7 border-2 text-center" style={{ borderColor: "#F39A1F" }} data-testid="lead-capture-form">
      <div className="flex items-center justify-center gap-2 mb-3">
        <Lock className="w-4 h-4 text-gold-deep" />
        <div className="text-[12px] font-overline uppercase tracking-wider-cap text-gold-deep">7-Day Free Trial</div>
      </div>
      <h3 className="text-3xl md:text-4xl uppercase text-red-wine leading-[1.05]" style={{ textWrap: "balance" }}>
        Join <Brand /> Free
      </h3>
      <p className="text-base text-foreground mt-3 max-w-md mx-auto">
        <strong className="text-red-wine">No credit card. 7 days free.</strong> Drop your email to create your account in 30 seconds.
      </p>
      <div className="grid md:grid-cols-2 gap-3 mt-5 text-left">
        <input required placeholder="Your name"
               className="px-4 py-3 rounded-md border-2 border-border bg-white text-base"
               value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
               data-testid="lead-name" />
        <input ref={emailRef} required type="email" placeholder="your@email.com"
               className="px-4 py-3 rounded-md border-2 border-border bg-white text-base"
               value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
               data-testid="lead-email" />
      </div>
      <button disabled={loading} type="submit"
              className="btn-primary w-full mt-4 inline-flex items-center justify-center gap-2"
              data-testid="lead-submit">
        <Gift className="w-4 h-4" /> {loading ? "Creating account…" : "Create My Free Account"}
      </button>
      <div className="text-[12px] tracking-wider-cap text-foreground mt-3">
        <Mail className="w-3 h-3 inline mr-1" /> No spam · Unsubscribe anytime · <Brand suffix=".com" />
      </div>
    </form>
  );
});

export default LeadCapture;
